import React, { cloneElement, forwardRef, ReactNode, useCallback } from 'react';
import Slider, { SliderProps, SliderRef } from 'rc-slider';
import 'rc-slider/assets/index.css';

import Tooltip from 'components/Tooltip/Tooltip';
import { HelpIcon } from 'components/SvgIcons';

import useSliderMarkConfig, { SliderMarkConfig } from './hooks/useSliderMarkConfig';
import Handle from './components/Handle/Handle';

import './SliderInput.scss';

export type Transform = (value: number | undefined) => number | undefined;
export type TooltipTrasnfrom = (value: number | undefined) => string;

const defaultTransformIn: Transform = (value) => value;
const defaultTransformOut: Transform = (value) => value;
const defaultTransformTooltip: TooltipTrasnfrom = (value) => (value === undefined ? '-' : String(value));

export interface SliderInputProps extends Omit<SliderProps<number>, 'marks'> {
  marks?: Record<string | number, SliderMarkConfig>;
  label?: ReactNode;
  noLabel?: boolean;
  id?: string;
  required?: boolean;
  hint?: ReactNode;
  variant?: 'standard' | 'filled';
  alwaysShowHandleTooltip?: boolean;
  transformIn?: Transform;
  transformOut?: Transform;
  transformTooltip?: TooltipTrasnfrom;
}

const SingleSlider = Slider as React.ForwardRefExoticComponent<SliderProps<number> & React.RefAttributes<SliderRef>>;

const SliderInput = forwardRef<SliderRef, SliderInputProps>(
  (
    {
      variant = 'standard',
      label,
      noLabel,
      id,
      className = '',
      required,
      hint,
      alwaysShowHandleTooltip,
      transformIn = defaultTransformIn,
      transformOut = defaultTransformOut,
      transformTooltip = defaultTransformTooltip,
      min,
      max,
      value,
      onChange,
      ...props
    },
    ref,
  ) => {
    const tMin = transformIn(min);
    const tMax = transformIn(max);

    const [marks, tooltips] = useSliderMarkConfig(props.marks, transformIn, min, max, tMin, tMax);

    const handleRender = useCallback<NonNullable<SliderProps['handleRender']>>(
      (node, { value: rValue }) => {
        const tValue = transformOut(rValue);
        const newProps = {
          ...node.props,
          children: (
            <div className="static-tooltip">
              <div className="static-tooltip-content">{transformTooltip(tValue)}</div>
            </div>
          ),
        };

        return (
          <Handle value={rValue} visible={false} tooltips={tooltips}>
            {cloneElement(node, newProps)}
          </Handle>
        );
      },
      [tooltips],
    );

    const alwaysShowHandleTooltipClass = alwaysShowHandleTooltip ? 'always-show-handle-tooltip' : '';

    return (
      <div className={`enkrateia-slider ${className} variant-${variant} ${alwaysShowHandleTooltipClass}`}>
        <label className={noLabel ? 'sr-only' : ''}>
          {label}
          {required && '*'}
          {hint && (
            <Tooltip tooltip={hint}>
              <HelpIcon color="#05445E" size={25} />
            </Tooltip>
          )}
        </label>
        <SingleSlider
          {...props}
          value={transformIn(value)}
          onChange={(v) => onChange?.(transformOut(v)!)}
          ref={ref}
          handleRender={handleRender}
          marks={marks}
          min={tMin}
          max={tMax}
        />
      </div>
    );
  },
);

export default SliderInput;
